import APIManager from "../../managers/APIManager";

export const AIVideoActions = {
    // List
    GET_AI_VIDEOS: "GET_AI_VIDEOS",
    SET_AI_VIDEOS_LOADING: "SET_AI_VIDEOS_LOADING",
    SET_AI_VIDEOS_ERROR: "SET_AI_VIDEOS_ERROR",
    RESET_AI_VIDEOS: "RESET_AI_VIDEOS",

    // Details
    GET_AI_VIDEO_DETAILS: "GET_AI_VIDEO_DETAILS",
    SET_AI_VIDEO_DETAILS_LOADING: "SET_AI_VIDEO_DETAILS_LOADING",
    SET_AI_VIDEO_DETAILS_ERROR: "SET_AI_VIDEO_DETAILS_ERROR",

    // Settings (templates + creation settings)
    GET_AI_VIDEO_TEMPLATES: "GET_AI_VIDEO_TEMPLATES",
    SET_AI_VIDEO_TEMPLATES_LOADING: "SET_AI_VIDEO_TEMPLATES_LOADING",
    SET_AI_VIDEO_TEMPLATES_ERROR: "SET_AI_VIDEO_TEMPLATES_ERROR",

    GET_AI_VIDEO_CREATION_SETTINGS: "GET_AI_VIDEO_CREATION_SETTINGS",
    SET_AI_VIDEO_CREATION_SETTINGS_LOADING:
        "SET_AI_VIDEO_CREATION_SETTINGS_LOADING",
    SET_AI_VIDEO_CREATION_SETTINGS_ERROR: "SET_AI_VIDEO_CREATION_SETTINGS_ERROR",
    SET_AI_VIDEO_CREATION_SETTINGS_SAVING:
        "SET_AI_VIDEO_CREATION_SETTINGS_SAVING",

    // Homepage summary
    GET_AI_VIDEO_HOMEPAGE_SUMMARY: "GET_AI_VIDEO_HOMEPAGE_SUMMARY",
    SET_AI_VIDEO_HOMEPAGE_LOADING: "SET_AI_VIDEO_HOMEPAGE_LOADING",
    SET_AI_VIDEO_HOMEPAGE_ERROR: "SET_AI_VIDEO_HOMEPAGE_ERROR",
};

export const AIVideoActionCreators = {
    // Paginated list
    getAIVideos:
        ({ siteVirgulId, page, perPage, isPublished }) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEOS_LOADING,
                    payload: true,
                });

                return APIManager.getAIVideos({
                    siteVirgulId,
                    page,
                    perPage,
                    isPublished,
                })
                    .then((res) => {
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEOS,
                            payload: {
                                data: res?.data || [],
                                totalItems: res?.totalItems || 0,
                                totalPages: res?.totalPages || 1,
                            },
                        });
                    })
                    .catch((error) => {
                        // Keep behavior backward compatible: on error expose empty list
                        console.error("Error fetching AI videos:", error);
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEOS_ERROR,
                            payload: error,
                        });
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEOS,
                            payload: {
                                data: [],
                                totalItems: 0,
                                totalPages: 1,
                            },
                        });
                    })
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEOS_LOADING,
                            payload: false,
                        });
                    });
            },

    // Single video details
    getAIVideoDetails:
        ({ siteVirgulId, videoId }) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEO_DETAILS_LOADING,
                    payload: true,
                });

                return APIManager.getAIVideo({
                    siteVirgulId,
                    videoId,
                })
                    .then((res) => {
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_DETAILS,
                            payload: res?.data || null,
                        });
                    })
                    .catch((error) => {
                        console.error("Error fetching AI video details:", error);
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_DETAILS_ERROR,
                            payload: error,
                        });
                        // On error, keep data as null so UI can show "video not found"
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_DETAILS,
                            payload: null,
                        });
                    })
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_DETAILS_LOADING,
                            payload: false,
                        });
                    });
            },

    deleteAIVideo:
        ({ siteVirgulId, videoId }) =>
            (dispatch) =>
                APIManager.deleteAIVideo({
                    siteVirgulId,
                    videoId,
                }).then((res) => {
                    // After delete, clear details
                    dispatch({
                        type: AIVideoActions.GET_AI_VIDEO_DETAILS,
                        payload: null,
                    });
                    return res;
                }),

    publishAIVideo:
        ({ siteVirgulId, videoId }) =>
            (dispatch, getState) =>
                APIManager.publishAIVideo({
                    siteVirgulId,
                    videoId,
                }).then((res) => {
                    const current = getState()?.aiVideo?.details?.data || {};
                    dispatch({
                        type: AIVideoActions.GET_AI_VIDEO_DETAILS,
                        payload: { ...current, isPublished: true },
                    });
                    return res;
                }),

    unpublishAIVideo:
        ({ siteVirgulId, videoId }) =>
            (dispatch, getState) =>
                APIManager.unpublishAIVideo({
                    siteVirgulId,
                    videoId,
                }).then((res) => {
                    const current = getState()?.aiVideo?.details?.data || {};
                    dispatch({
                        type: AIVideoActions.GET_AI_VIDEO_DETAILS,
                        payload: { ...current, isPublished: false },
                    });
                    return res;
                }),

    // Settings: templates + creation settings
    getAIVideoTemplates:
        ({ siteVirgulId }) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEO_TEMPLATES_LOADING,
                    payload: true,
                });

                return APIManager.getAIVideoTemplates({
                    siteVirgulId,
                })
                    .then((res) => {
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_TEMPLATES,
                            payload: res?.data || [],
                        });
                    })
                    .catch((error) => {
                        console.error("Error fetching AI video templates:", error);
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_TEMPLATES_ERROR,
                            payload: error,
                        });
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_TEMPLATES,
                            payload: [],
                        });
                    })
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_TEMPLATES_LOADING,
                            payload: false,
                        });
                    });
            },

    getAIVideoCreationSettings:
        ({ siteVirgulId }) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEO_CREATION_SETTINGS_LOADING,
                    payload: true,
                });

                return APIManager.getAIVideoCreationSettings({
                    siteVirgulId,
                })
                    .then((res) => {
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_CREATION_SETTINGS,
                            payload: res?.data || null,
                        });
                    })
                    .catch((error) => {
                        console.error("Error fetching AI video creation settings:", error);
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_CREATION_SETTINGS_ERROR,
                            payload: error,
                        });
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_CREATION_SETTINGS,
                            payload: null,
                        });
                    })
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_CREATION_SETTINGS_LOADING,
                            payload: false,
                        });
                    });
            },

    saveAIVideoCreationSettings:
        (payload) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEO_CREATION_SETTINGS_SAVING,
                    payload: true,
                });

                return APIManager.saveAIVideoCreationSettings(payload)
                    .then((res) => res)
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_CREATION_SETTINGS_SAVING,
                            payload: false,
                        });
                    });
            },

    // Homepage summary (live + past videos)
    getAIVideoHomepageSummary:
        ({ siteVirgulId }) =>
            (dispatch) => {
                dispatch({
                    type: AIVideoActions.SET_AI_VIDEO_HOMEPAGE_LOADING,
                    payload: true,
                });

                const baseParams = {
                    siteVirgulId,
                    page: 1,
                    perPage: 4,
                };

                return APIManager.getAIVideos({
                    ...baseParams,
                    isPublished: false,
                })
                    .then((pastRes) => {
                        const pastData = {
                            pastVideos: pastRes?.data || [],
                            pastVideosTotal: pastRes?.totalItems || 0,
                        };

                        return APIManager.getAIVideos({
                            ...baseParams,
                            isPublished: true,
                        })
                            .then((liveRes) => {
                                const liveData = {
                                    liveVideos: liveRes?.data || [],
                                    liveVideosTotal: liveRes?.totalItems || 0,
                                };

                                dispatch({
                                    type: AIVideoActions.GET_AI_VIDEO_HOMEPAGE_SUMMARY,
                                    payload: {
                                        ...pastData,
                                        ...liveData,
                                    },
                                });
                            })
                            .catch((error) => {
                                console.error("Error fetching live AI videos:", error);
                                dispatch({
                                    type: AIVideoActions.GET_AI_VIDEO_HOMEPAGE_SUMMARY,
                                    payload: {
                                        ...pastData,
                                        liveVideos: [],
                                        liveVideosTotal: 0,
                                    },
                                });
                            });
                    })
                    .catch((error) => {
                        console.error("Error fetching AI videos:", error);
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_HOMEPAGE_ERROR,
                            payload: error,
                        });
                        dispatch({
                            type: AIVideoActions.GET_AI_VIDEO_HOMEPAGE_SUMMARY,
                            payload: {
                                liveVideos: [],
                                liveVideosTotal: 0,
                                pastVideos: [],
                                pastVideosTotal: 0,
                            },
                        });
                    })
                    .finally(() => {
                        dispatch({
                            type: AIVideoActions.SET_AI_VIDEO_HOMEPAGE_LOADING,
                            payload: false,
                        });
                    });
            },
};
